import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { ISiteAdminProfile } from '../../users/serverApi/requests/GetSiteAdminProfilesRequest';
import { SiteUsers } from './SiteUsers';

/**
 * Caches all admin profiles for the current site. Prefetch on the users page so
 * the Change site role and Add user dialogs have profiles ready.
 */
export class AdminProfilesResource {
  public static readonly instance = new AdminProfilesResource();

  private profiles: ISiteAdminProfile[] = [];
  private loaded = false;
  private loadPromise: Promise<ISiteAdminProfile[]> | null = null;
  private loadedForSiteLuid = '';

  /** Clears cached profiles so next access refetches for current site context. */
  public reset(): void {
    this.profiles = [];
    this.loaded = false;
    this.loadPromise = null;
    this.loadedForSiteLuid = '';
  }

  /** Call when the users page loads so profiles are ready for dialogs. */
  public prefetch(): void {
    this.resetIfSiteChanged();
    if (!getSessionManagementServiceInstance().isAdminProfileUiEnabled() || this.loadPromise !== null) {
      return;
    }
    const siteLuid = this.getCurrentSiteLuid();
    this.loadedForSiteLuid = siteLuid;
    this.loadPromise = this.fetchAll(siteLuid);
  }

  /** Returns cached profiles or the in-flight load. Resolves to [] if feature is off or load failed. */
  public getProfiles(): Promise<ISiteAdminProfile[]> {
    this.resetIfSiteChanged();
    if (this.loaded) {
      return Promise.resolve(this.profiles);
    }
    if (!getSessionManagementServiceInstance().isAdminProfileUiEnabled()) {
      return Promise.resolve([]);
    }
    if (this.loadPromise === null) {
      this.prefetch();
    }
    return this.loadPromise!;
  }

  private async fetchAll(siteLuidAtRequest: string): Promise<ISiteAdminProfile[]> {
    const all: ISiteAdminProfile[] = [];
    let startIndex = 0;
    const pageSize = 100;

    try {
      let hasMore = true;
      while (hasMore) {
        const result = await SiteUsers.instance.getSiteAdminProfiles({
          page: { startIndex, maxItems: pageSize }
        });
        for (const p of result.adminProfiles) {
          all.push(p);
        }
        if (all.length >= result.pagination.totalAvailable) {
          hasMore = false;
        } else {
          startIndex = all.length;
        }
      }
      // Ignore stale in-flight responses if site changed during request.
      if (this.loadedForSiteLuid === siteLuidAtRequest) {
        this.profiles = all;
        this.loaded = true;
      }
    } catch {
      // Leave profiles empty on error
    }
    return this.profiles;
  }

  private resetIfSiteChanged(): void {
    const currentSiteLuid = this.getCurrentSiteLuid();
    if (this.loadedForSiteLuid !== '' && currentSiteLuid !== '' && this.loadedForSiteLuid !== currentSiteLuid) {
      this.reset();
    }
  }

  private getCurrentSiteLuid(): string {
    try {
      return getSessionManagementServiceInstance().sessionSiteLuid() || '';
    } catch {
      return '';
    }
  }
}
